/**
 * Validation / Qualification module hooks (C-7). Backed by
 * /api/v1/validation-events (real Postgres, validation.validation_events).
 * A validation event links to deviations / CAPAs via the C-4 event graph
 * (the RelatedRecords panel on the detail screen).
 */
import {
  useMutation, useQuery, useQueryClient,
  type UseMutationResult, type UseQueryResult,
} from '@tanstack/react-query';
import { apiFetch } from '../../api/client';

export interface ValidationHistoryRow {
  event_kind: string; from_status: string | null; to_status: string | null;
  note: string | null; at_utc: string; actor_name: string | null;
}
export interface ValidationEvent {
  validationId: string;
  validationNumber: string;
  title: string;
  validationType: string;
  systemRef: string | null;
  protocolNumber: string | null;
  approach: string | null;
  status: string;
  outcome: string | null;
  protocolApprovedDate: string | null;
  executionStartDate: string | null;
  executionEndDate: string | null;
  reportApprovedDate: string | null;
  targetDate: string | null;
  ownerName: string | null;
  notes: string | null;
  openedAt: string;
  closedAt: string | null;
  history?: ValidationHistoryRow[];
}
export interface ValidationStats {
  total: number; open: number; in_execution: number; protocol_draft: number; approved: number; overdue: number;
}
export interface ValidationListResp { items: ValidationEvent[]; total: number; stats: ValidationStats }

export interface ValidationFilters { type?: string; status?: string }

export function useValidationsQuery(filters: ValidationFilters = {}): UseQueryResult<ValidationListResp> {
  const qs = new URLSearchParams();
  if (filters.type) qs.set('type', filters.type);
  if (filters.status) qs.set('status', filters.status);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return useQuery<ValidationListResp>({
    queryKey: ['validation-events', filters],
    queryFn: ({ signal }) => apiFetch<ValidationListResp>(`/api/v1/validation-events${suffix}`, { signal }),
    staleTime: 30_000,
  });
}
export function useValidationDetailQuery(id: string | undefined): UseQueryResult<ValidationEvent> {
  return useQuery<ValidationEvent>({
    queryKey: ['validation-events', 'detail', id],
    queryFn: ({ signal }) => apiFetch<ValidationEvent>(`/api/v1/validation-events/${encodeURIComponent(id!)}`, { signal }),
    enabled: !!id,
    staleTime: 30_000,
  });
}

export interface NewValidationInput {
  title: string; validationType?: string; systemRef?: string; protocolNumber?: string; approach?: string; targetDate?: string;
}
export function useCreateValidation(): UseMutationResult<ValidationEvent, Error, NewValidationInput> {
  const qc = useQueryClient();
  return useMutation<ValidationEvent, Error, NewValidationInput>({
    mutationFn: (body) => apiFetch<ValidationEvent>('/api/v1/validation-events', { method: 'POST', body }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['validation-events'] }),
  });
}
export function useValidationTransition(id: string | undefined): UseMutationResult<unknown, Error, { toStatus: string; note?: string; outcome?: string }> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { toStatus: string; note?: string; outcome?: string }) =>
      apiFetch(`/api/v1/validation-events/${encodeURIComponent(id!)}/transition`, { method: 'POST', body }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['validation-events'] }),
  });
}

// Mirror of the server FSM (validation.validation_events status machine).
export const VALIDATION_FSM: Record<string, string[]> = {
  PROTOCOL_DRAFT: ['PROTOCOL_APPROVED', 'CANCELLED'],
  PROTOCOL_APPROVED: ['IN_EXECUTION', 'CANCELLED'],
  IN_EXECUTION: ['REPORT_DRAFT', 'ON_HOLD', 'CANCELLED'],
  ON_HOLD: ['IN_EXECUTION', 'CANCELLED'],
  REPORT_DRAFT: ['APPROVED', 'IN_EXECUTION'],
  APPROVED: ['CLOSED'],
  CLOSED: [],
  CANCELLED: [],
};
export const VAL_STATUS_LABEL: Record<string, string> = {
  PROTOCOL_DRAFT: 'Protocol draft', PROTOCOL_APPROVED: 'Protocol approved', IN_EXECUTION: 'In execution',
  ON_HOLD: 'On hold', REPORT_DRAFT: 'Report draft', APPROVED: 'Approved', CLOSED: 'Closed', CANCELLED: 'Cancelled',
};
export const VAL_TYPE_LABEL: Record<string, string> = {
  PROCESS: 'Process (PV)', CLEANING: 'Cleaning (CV)', EQUIPMENT: 'Equipment (IQ/OQ/PQ)',
  COMPUTER: 'Computer System (CSV)', HVAC: 'HVAC', METHOD: 'Analytical Method', UTILITY: 'Utility',
};
export const VAL_OUTCOME_LABEL: Record<string, string> = {
  PASS: 'Pass', PASS_WITH_DEVIATIONS: 'Pass (with deviations)', FAIL: 'Fail',
};
